import {Component, computed, inject, signal, ViewChild} from '@angular/core';
import {finalize} from 'rxjs/operators';
import {DxDataGridComponent} from 'devextreme-angular';
import {confirm} from 'devextreme/ui/dialog';
import {FermetureClientService} from '../../core/services/entities/fermeture-client.service';
import {ToastService} from '../../core/services/technique/toast.service';
import {MSG_KEY, MSG_SEVERITY} from '../../core/constants';
import {FermetureJourSemaineDTO} from '../../core/dtos/client/fermeture/fermeture-jour-semaine-dto';
import {FermetureJourSemainePlanProdErreurDTO} from '../../core/dtos/client/fermeture/fermeture-jour-semaine-plan-prod-erreur-dto';
import {FermeturePeriodeDTO} from '../../core/dtos/client/fermeture/fermeture-periode-dto';
import {FermeturePeriodePlanProdErreurDTO} from '../../core/dtos/client/fermeture/fermeture-periode-plan-prod-erreur-dto';
import {ButtonThemeEnum} from '../../shared/ui/button/button-theme-enum';
import {FermetureTabEnum} from './fermeture-tab.enum';
import {FermetureAddAreaComponent} from './add-area/fermeture-add-area.component';

@Component({
  selector: 'yo-fermeture-clients',
  templateUrl: './fermeture-clients.component.html',
  styleUrls: ['./fermeture-clients.component.scss'],
  standalone: false
})
export class FermetureClientsComponent {

  protected readonly ButtonThemeEnum = ButtonThemeEnum;
  protected readonly FermetureTabEnum = FermetureTabEnum;

  readonly fermetureClientSvc = inject(FermetureClientService);
  private readonly toastSvc = inject(ToastService);
  @ViewChild(FermetureAddAreaComponent) addAreaRef?: FermetureAddAreaComponent;
  @ViewChild('gridJour') gridJour?: DxDataGridComponent;
  @ViewChild('gridPeriode') gridPeriode?: DxDataGridComponent;

  activeTab = signal<FermetureTabEnum>(FermetureTabEnum.JOUR);
  activeTabIndex = computed(() => this.activeTab() === FermetureTabEnum.JOUR ? 0 : 1);
  tabItems = [{text: 'Fermeture récurrente'}, {text: 'Fermeture ponctuelle'}];

  onTabChange = (e: any): void => {
    const newTab = e.itemIndex === 0 ? FermetureTabEnum.JOUR : FermetureTabEnum.PERIODE;
    if (newTab !== this.activeTab()) {
      this.addAreaRef?.reset();
      this.closeAddArea();
    }
    this.activeTab.set(newTab);
  };

  isAddAreaVisible = signal(false);

  openAddArea = (): void => {this.isAddAreaVisible.set(true);};

  closeAddArea = (): void => this.isAddAreaVisible.set(false);

  isSaving = signal(false);
  isPreviewJourVisible = signal(false);
  isPreviewPeriodeVisible = signal(false);
  pendingJourLines = signal<FermetureJourSemaineDTO[]>([]);
  pendingPeriodeLines = signal<FermeturePeriodeDTO[]>([]);
  planProdErreurs = signal<FermeturePeriodePlanProdErreurDTO[]>([]);
  isPlanProdErrorPopupVisible = signal(false);

  planProdErreursJour = signal<FermetureJourSemainePlanProdErreurDTO[]>([]);
  isPlanProdErrorJourPopupVisible = signal(false);

  onJourLinesReady = (lines: FermetureJourSemaineDTO[]): void => {
    this.pendingJourLines.set(lines);
    this.isPreviewJourVisible.set(true);
  };

  onPeriodeLinesReady = (lines: FermeturePeriodeDTO[]): void => {
    this.pendingPeriodeLines.set(lines);
    this.isPreviewPeriodeVisible.set(true);
  };

  closePreviewJour = (): void => {
    this.isPreviewJourVisible.set(false);
    this.pendingJourLines.set([]);
  };

  closePreviewPeriode = (): void => {
    this.isPreviewPeriodeVisible.set(false);
    this.pendingPeriodeLines.set([]);
  };


  hasSelection = (): boolean => {
    if (this.activeTab() === FermetureTabEnum.JOUR)
      return (this.gridJour?.selectedRowKeys?.length ?? 0) > 0;
    return (this.gridPeriode?.selectedRowKeys?.length ?? 0) > 0;
  };

  deleteSelected = async (): Promise<void> => {
    const confirmed = await confirm('Confirmer la suppression des fermetures sélectionnées ?', 'Suppression');
    if (!confirmed) return;
    if (this.activeTab() === FermetureTabEnum.JOUR)
      this.deleteJours(this.gridJour?.selectedRowKeys ?? []);
    else
      this.deletePeriodes(this.gridPeriode?.selectedRowKeys ?? []);
  };


  savePreviewJour = async (): Promise<void> => {
    if (!await this.confirmSave()) return;
    const lines = this.pendingJourLines();
    this.closePreviewJour();
    this.closeAddArea();
    this.isSaving.set(true);
    this.fermetureClientSvc.saveFermeturesJour(lines).pipe(
      finalize(() => this.isSaving.set(false))
    ).subscribe({
      next: result => {
        if (result.one.saved.length > 0) {
          this.fermetureClientSvc.fermeturesJourResource.update(current => {
            const list = [...(current ?? [])];
            for (const newLine of result.one.saved) {
              const idx = list.findIndex(l =>
                l.clientId === newLine.clientId &&
                l.pointLivraisonId === newLine.pointLivraisonId &&
                l.prestationId === newLine.prestationId
              );
              if (idx >= 0)
                list[idx] = {...list[idx], jours: [...new Set([...list[idx].jours, ...newLine.jours])]};
              else
                list.push(newLine);
            }
            return list;
          });
          this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, 'Fermetures récurrentes enregistrées');
        }
        if (result.one.erreurs.length > 0) {
          this.planProdErreursJour.set(result.one.erreurs);
          this.isPlanProdErrorJourPopupVisible.set(true);
        }
      }
    });
  };

  savePreviewPeriode = async (): Promise<void> => {
    if (!await this.confirmSave()) return;
    const lines = this.pendingPeriodeLines();
    this.closePreviewPeriode();
    this.closeAddArea();
    this.isSaving.set(true);
    this.fermetureClientSvc.saveFermeturesPeriode(lines).pipe(
      finalize(() => this.isSaving.set(false))
    ).subscribe({
      next: result => {
        if (result.one.saved.length > 0) {
          const savedIds = new Set(result.one.saved.map(s => s.id));
          this.fermetureClientSvc.fermeturesPeriodeResource.update(current =>
            [...(current ?? []).filter(l => !savedIds.has(l.id)), ...result.one.saved]
          );
          this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, 'Fermetures ponctuelles enregistrées');
        }
        if (result.one.erreurs.length > 0) {
          this.planProdErreurs.set(result.one.erreurs);
          this.isPlanProdErrorPopupVisible.set(true);
        }
      }
    });
  };

  private confirmSave = (): Promise<boolean> =>
    confirm(
      'Si des effectifs sont déjà saisis sur des dates fermées alors ils seront supprimés. Souhaitez-vous continuer ?',
      'Confirmation'
    );


  private deleteJours = (fermetureIds: number[]): void => {
    if (!fermetureIds.length) return;
    this.fermetureClientSvc.deleteFermeturesJour(fermetureIds).subscribe({
      complete: () => {
        const ids = new Set(fermetureIds);
        this.fermetureClientSvc.fermeturesJourResource.update(current =>
          (current ?? []).filter(l => !ids.has(l.cmcPlcId))
        );
        this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, 'Fermeture(s) supprimée(s)');
      }
    });
  };

  private deletePeriodes = (fermetureIds: number[]): void => {
    if (!fermetureIds.length) return;
    this.fermetureClientSvc.deleteFermeturesPeriode(fermetureIds).subscribe({
      complete: () => {
        const idSet = new Set(fermetureIds);
        this.fermetureClientSvc.fermeturesPeriodeResource.update(current =>
          (current ?? []).filter(l => !idSet.has(l.id))
        );
        this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, 'Fermeture(s) supprimée(s)');
      }
    });
  };
}
